import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import Drawer from '@mui/material/Drawer'
import Popper from '@mui/material/Popper'
import CloseIcon from '@mui/icons-material/Close'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import MenuIcon from '@mui/icons-material/Menu'
import SearchIcon from '@mui/icons-material/Search'
import { useFormatMessage } from '@/translations/useFormatMessage'
import RefContext from '@/contexts/RefContext'
import { useMenuCategories } from '@/pages/menu/hooks/useMenuCategories'
import { useMenuItems } from '@/pages/menu/hooks/useMenuItems'
import type { MenuCategory } from '@/shared/api/menu'
import CategoryNavSkeleton from './CategoryNavSkeleton'
import {
	HamburgerButton,
	clearIconSx,
	LiveRegion,
	primaryIconSx,
	moreIconSx,
	searchIconSx,
	SheetPaperSx,
	MeasureRow,
	MobilePillRow,
	MobileSearchBar,
	MobileSearchRow,
	MobileSheetCancel,
	MobileSheetCloseButton,
	MobileSheetHeader,
	MobileSheetItem,
	MobileSheetList,
	MobileSheetTitle,
	MoreButton,
	MoreMenu,
	MoreMenuItem,
	NavBar,
	PillButton,
	PillsRow,
	PillsWrap,
	SearchBar,
	SearchClearButton,
	SearchInput,
} from './CategoryNav.styled'

const DESKTOP_PILL_GAP_PX = 8

const MENU_SEARCH_MAX_LENGTH = 64

interface CategoryNavProps {
	categories?: MenuCategory[]
	isLoading?: boolean
	scheduleId?: string
	onSearch?: (term: string) => void
	clearSearchRef?: React.MutableRefObject<(() => void) | null>
	searchTerm?: string
}

export default function CategoryNav({
	categories: categoriesProp,
	isLoading: isLoadingProp,
	scheduleId = '',
	onSearch,
	clearSearchRef,
	searchTerm = '',
}: CategoryNavProps) {
	// scheduleId is always provided by MenuLayout — the single source of truth.
	// useContext here only to satisfy useMenuCategories; the prop drives what gets fetched.
	const locationRef = useContext(RefContext) ?? ''
	const { data, isLoading: isFetching } = useMenuCategories(locationRef, scheduleId)
	// useMenuItems is called here (same query key as MenuPage) to gate the IntersectionObserver
	// setup until the <CategorySection> DOM nodes exist. No extra network request — TanStack
	// Query serves from cache once MenuPage's fetch resolves.
	const { isLoading: itemsLoading, data: itemsData } = useMenuItems(locationRef, scheduleId)
	const sectionsReady = !itemsLoading && itemsData !== undefined

	const categories = useMemo(
		() => categoriesProp ?? data?.categories ?? [],
		[categoriesProp, data?.categories],
	)
	const isLoading = isLoadingProp ?? isFetching
	const { t } = useFormatMessage()

	const [activeCategory, setActiveCategory] = useState<string | null>(null)
	const [mobileSearchFocused, setMobileSearchFocused] = useState(false)
	const [mobileSearchValue, setMobileSearchValue] = useState('')
	const [desktopSearchValue, setDesktopSearchValue] = useState('')
	const mobileSearchInputRef = useRef<HTMLInputElement | null>(null)
	const mobileSearchRowRef = useRef<HTMLDivElement | null>(null)
	const desktopSearchInputRef = useRef<HTMLInputElement | null>(null)

	// Keep the user's active selection if it still exists in the current categories list
	const resolvedActive =
		activeCategory && categories.some((c) => c.ref === activeCategory)
			? activeCategory
			: (categories[0]?.ref ?? null)

	const [visibleCount, setVisibleCount] = useState<number | null>(null)
	const [moreOpen, setMoreOpen] = useState(false)
	const [sheetOpen, setSheetOpen] = useState(false)
	const [announcement, setAnnouncement] = useState('')
	// ref callback keeps moreAnchorEl in state so Popper reads a stable value during render.
	// moreAnchorElRef mirrors the same node so keyboard handlers always read the current
	// mounted element rather than a closure-captured state value that may be stale after a
	// resize-triggered remount of MoreButton.
	const [moreAnchorEl, setMoreAnchorEl] = useState<HTMLButtonElement | null>(null)
	const moreAnchorElRef = useRef<HTMLButtonElement | null>(null)
	const moreButtonRef = useCallback((el: HTMLButtonElement | null) => {
		setMoreAnchorEl(el)
		moreAnchorElRef.current = el
	}, [])

	const pillsWrapRef = useRef<HTMLDivElement | null>(null)
	const measureRowRef = useRef<HTMLDivElement | null>(null)
	const moreMenuRef = useRef<HTMLDivElement | null>(null)
	const mobilePillsRef = useRef<HTMLDivElement | null>(null)
	// Carries a pending scroll-to-category ref across the render cycle when search is cleared.
	// handleCategoryClick sets this before calling onSearch('') so the useEffect below can
	// scroll once React has re-rendered the full category DOM.
	const pendingScrollRef = useRef<string | null>(null)
	// Carries a pending scroll ref for the sheet-close case. scrollIntoView must not fire
	// while the MUI Drawer scroll lock is active — the lock is only removed ~195ms after
	// the Slide exit animation ends. A useEffect watching sheetOpen fires the scroll after
	// a 300ms delay, by which time the lock has been released.
	const pendingSheetScrollRef = useRef<string | null>(null)
	const isClickScrollingRef = useRef(false)
	const clickScrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	// Tracks when the last click-scroll started (ms). IntersectionObserver entries
	// queued before this timestamp are stale and must be ignored even if the ref flag
	// was not yet set when they were dispatched.
	const clickScrollStartRef = useRef<number>(0)
	// Holds the current onScrollIdle listener reference so rapid clicks can remove
	// the previous listener before registering a new one — prevents accumulation.
	const scrollIdleListenerRef = useRef<(() => void) | null>(null)

	useEffect(() => {
		if (categories.length === 0 || !sectionsReady) return

		const observer = new IntersectionObserver(
			(entries) => {
				// Suppress observer updates during and immediately after a click-initiated scroll
				// or a scroll-lock restoration (STO-3781: closing AddItemPage/EditItemPage fires a
				// programmatic window.scrollTo that must not reset the active category).
				if (isClickScrollingRef.current) return
				const intersecting = entries.find(
					(e) => e.isIntersecting && e.time >= clickScrollStartRef.current,
				)
				if (intersecting) {
					setActiveCategory(intersecting.target.id.replace('category-', ''))
				}
			},
			{ threshold: 0, rootMargin: '-20% 0px -70% 0px' },
		)

		for (const cat of categories) {
			const el = document.getElementById(`category-${cat.ref}`)
			if (el) observer.observe(el)
		}

		// The last category may be too short to ever enter the IntersectionObserver's
		// active band (-20% top / -70% bottom). When the user reaches the bottom of the
		// page, force the last category active — matching Ember's offsetTop scroll logic.
		function handleScroll() {
			if (isClickScrollingRef.current) return
			// iOS rubber-band overscroll makes scrollY temporarily negative — not near the bottom.
			if (window.scrollY < 0) return
			// When the filtered page is shorter than the viewport it cannot be scrolled, so the
			// IntersectionObserver is the sole source of truth; the near-bottom heuristic must not fire.
			if (document.body.scrollHeight <= window.innerHeight) return
			const nearBottom = window.innerHeight + window.scrollY >= document.body.scrollHeight - 4
			if (nearBottom) {
				const lastCategory = categories[categories.length - 1]
				if (lastCategory) setActiveCategory(lastCategory.ref)
			}
		}

		window.addEventListener('scroll', handleScroll, { passive: true })

		return () => {
			observer.disconnect()
			window.removeEventListener('scroll', handleScroll)
		}
	}, [categories, sectionsReady, searchTerm])

	// STO-3781: when a scroll lock is released (e.g. AddItemPage/EditItemPage closes),
	// useScrollLock fires window.scrollTo to restore the saved position. That emits a
	// real scroll event which would reset the active category via the observer and
	// handleScroll above. This effect is unconditional — it must be active regardless of
	// whether sectionsReady is true, since the drawer can close before sections finish loading.
	// Cancel any in-flight scrollIdleListener before starting the suppression window so a
	// rapid nav-click followed immediately by a drawer close doesn't leave a stale listener.
	useEffect(() => {
		function handleScrollLockRestore() {
			if (scrollIdleListenerRef.current) {
				window.removeEventListener('scroll', scrollIdleListenerRef.current)
				scrollIdleListenerRef.current = null
			}
			if (clickScrollTimerRef.current) clearTimeout(clickScrollTimerRef.current)
			clickScrollStartRef.current = performance.now()
			isClickScrollingRef.current = true
			clickScrollTimerRef.current = setTimeout(() => {
				isClickScrollingRef.current = false
			}, 300)
		}

		window.addEventListener('scrolllock:restore', handleScrollLockRestore)
		return () => {
			window.removeEventListener('scrolllock:restore', handleScrollLockRestore)
		}
	}, [])

	useEffect(() => {
		if (!clearSearchRef) return
		clearSearchRef.current = () => {
			if (mobileSearchInputRef.current) {
				mobileSearchInputRef.current.value = ''
			}
			if (desktopSearchInputRef.current) {
				desktopSearchInputRef.current.value = ''
			}
			setMobileSearchFocused(false)
			setMobileSearchValue('')
			setDesktopSearchValue('')
			onSearch?.('')
		}
		return () => {
			clearSearchRef.current = null
		}
	}, [clearSearchRef, onSearch])

	// Measures real pill widths from the hidden MeasureRow to determine how many
	// pills fit in the available container width before showing the More button
	const recalculate = useCallback(() => {
		const wrap = pillsWrapRef.current
		const measure = measureRowRef.current
		if (!wrap || !measure) return

		const available = wrap.clientWidth
		if (available <= 0) return

		const measureButtons = measure.querySelectorAll<HTMLButtonElement>('[data-measure]')
		if (measureButtons.length === 0) return

		const moreWidth = measureButtons[measureButtons.length - 1]?.offsetWidth ?? 0
		const categoryWidths = Array.from(measureButtons)
			.slice(0, -1)
			.map((btn) => btn.offsetWidth)

		if (
			!categoryWidths.length ||
			categoryWidths.length !== categories.length ||
			categoryWidths.some((w) => w <= 0)
		)
			return

		const total = categories.length
		let used = 0
		let count = 0

		for (let i = 0; i < total; i++) {
			const pillWidth = categoryWidths[i] ?? 0
			const gapBefore = count > 0 ? DESKTOP_PILL_GAP_PX : 0
			const remainingAfter = total - (i + 1)
			const reserveMore = remainingAfter > 0 ? DESKTOP_PILL_GAP_PX + moreWidth : 0

			if (used + gapBefore + pillWidth + reserveMore <= available) {
				used += gapBefore + pillWidth
				count = i + 1
			} else {
				break
			}
		}

		setVisibleCount(Math.max(count, 1))
	}, [categories])

	useEffect(() => {
		if (categories.length === 0) return
		const wrap = pillsWrapRef.current
		if (!wrap) return
		// PillsWrap is display:none on mobile — clientWidth is 0.
		// Skip the measurement reset entirely; MobilePillRow handles its own scrolling.
		if (wrap.clientWidth === 0) return
		setVisibleCount(null)
		const observer = new ResizeObserver(recalculate)
		observer.observe(wrap)
		recalculate()
		return () => observer.disconnect()
	}, [categories, recalculate])

	useEffect(() => {
		const nav = mobilePillsRef.current
		if (!nav) return
		const active = nav.querySelector<HTMLElement>('[aria-current="page"]')
		if (!active) return
		const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
		active.scrollIntoView({
			inline: 'nearest',
			block: 'nearest',
			behavior: reducedMotion ? 'auto' : 'smooth',
		})
	}, [activeCategory])

	useEffect(() => {
		if (!moreOpen) return
		function onClose() {
			setMoreOpen(false)
		}
		window.addEventListener('scroll', onClose, true)
		window.addEventListener('resize', onClose)
		return () => {
			window.removeEventListener('scroll', onClose, true)
			window.removeEventListener('resize', onClose)
		}
	}, [moreOpen])

	// Roving tabindex for desktop pill nav — Arrow Left/Right/Home/End move focus within
	// the PillsWrap without leaving the nav group. Tab moves out normally.
	const handleDesktopPillKeyDown = useCallback((e: React.KeyboardEvent<HTMLButtonElement>) => {
		if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return
		e.preventDefault()
		const wrap = pillsWrapRef.current
		if (!wrap) return
		// Collect all interactive pill/more buttons — excludes the hidden MeasureRow
		// buttons which have both tabIndex=-1 and data-measure. Real pills also have
		// tabIndex=-1 when not active, so we exclude by data-measure instead.
		const focusable = Array.from(
			wrap.querySelectorAll<HTMLButtonElement>('button:not([data-measure])'),
		)
		if (focusable.length === 0) return
		// e.currentTarget is the button that received the keydown — more reliable than
		// document.activeElement which can shift between event dispatch and this line.
		const current = e.currentTarget
		const idx = focusable.indexOf(current)
		let next: HTMLButtonElement | undefined
		if (e.key === 'ArrowRight') next = focusable[(idx + 1) % focusable.length]
		else if (e.key === 'ArrowLeft')
			next = focusable[(idx - 1 + focusable.length) % focusable.length]
		else if (e.key === 'Home') next = focusable[0]
		else if (e.key === 'End') next = focusable[focusable.length - 1]
		next?.focus()
	}, [])

	// More button: ArrowDown opens the dropdown. Focus is moved into the first menu item
	// by the useEffect below once React has committed the Popper to the DOM — a rAF is
	// not reliable in React 19 concurrent mode because state updates do not flush synchronously.
	const handleMoreButtonKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLButtonElement>) => {
			if (e.key === 'ArrowDown') {
				e.preventDefault()
				setMoreOpen(true)
			} else {
				handleDesktopPillKeyDown(e)
			}
		},
		[handleDesktopPillKeyDown],
	)

	// Move focus directly into the first menuitem after the Popper DOM is committed.
	// Focusing a role="menuitem" directly is what causes NVDA to switch from browse mode
	// to application mode — focusing the container div first is insufficient.
	useEffect(() => {
		if (!moreOpen) return
		const first = moreMenuRef.current?.querySelector<HTMLButtonElement>('[role="menuitem"]')
		first?.focus()
	}, [moreOpen])

	// More dropdown: ArrowDown/Up navigate items; Escape/Tab close and return focus to More button.
	const handleMoreMenuKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
		const menu = moreMenuRef.current
		if (!menu) return
		const items = Array.from(menu.querySelectorAll<HTMLButtonElement>('[role="menuitem"]'))
		if (items.length === 0) return
		const current = e.target as HTMLButtonElement
		const idx = items.indexOf(current)

		if (e.key === 'ArrowDown') {
			e.preventDefault()
			items[(idx + 1) % items.length]?.focus()
		} else if (e.key === 'ArrowUp') {
			e.preventDefault()
			// idx === -1 when focus is on the container: wrap to last item
			items[idx === -1 ? items.length - 1 : (idx - 1 + items.length) % items.length]?.focus()
		} else if (e.key === 'Escape') {
			e.preventDefault()
			setMoreOpen(false)
			moreAnchorElRef.current?.focus()
		} else if (e.key === 'Tab') {
			// Prevent native Tab from focusing a soon-to-be-removed Popper item (which
			// would drop focus to <body> on unmount). Return focus to the More button first.
			e.preventDefault()
			setMoreOpen(false)
			moreAnchorElRef.current?.focus()
		}
	}, [])

	// Raise the click-scroll flag on pointerdown so the IntersectionObserver is suppressed
	// before the scroll begins. On iOS Safari, click fires ~300ms after touchstart — without
	// this the observer can briefly activate the wrong category during that window.
	const handleCategoryPointerDown = useCallback(() => {
		if (clickScrollTimerRef.current) clearTimeout(clickScrollTimerRef.current)
		clickScrollStartRef.current = performance.now()
		isClickScrollingRef.current = true
		// Safety fallback: release flag if click never fires (e.g. pointer cancelled by scroll)
		clickScrollTimerRef.current = setTimeout(() => {
			isClickScrollingRef.current = false
		}, 1500)
	}, [])

	const handleCategoryClick = useCallback(
		(categoryRef: string, categoryName: string) => {
			// If search is active, clear it first so the full category DOM is restored before
			// we attempt scrollIntoView — the target section may not exist while search filters it out.
			if (searchTerm) {
				onSearch?.('')
				if (mobileSearchInputRef.current) mobileSearchInputRef.current.value = ''
				if (desktopSearchInputRef.current) desktopSearchInputRef.current.value = ''
				setMobileSearchFocused(false)
				setMobileSearchValue('')
				setDesktopSearchValue('')
			}

			setActiveCategory(categoryRef)
			// Reset first so re-clicking the same category still triggers a screen reader announcement
			setAnnouncement('')
			requestAnimationFrame(() => {
				setAnnouncement(
					t('component:categoryNav:categorySelected:announcement', { categoryName }),
				)
			})
			setMoreOpen(false)
			setSheetOpen(false)
			const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
			// isClickScrollingRef suppresses the IntersectionObserver while the page scrolls
			// to the target section. We keep the flag raised until scroll has been idle for
			// SCROLL_IDLE_MS — this handles both the programmatic smooth-scroll animation and
			// any residual momentum scroll from the user's finger, which can outlast a fixed timer.
			const SCROLL_IDLE_MS = reducedMotion ? 32 : 150
			if (clickScrollTimerRef.current) clearTimeout(clickScrollTimerRef.current)
			clickScrollStartRef.current = performance.now()
			isClickScrollingRef.current = true

			function onScrollIdle() {
				if (clickScrollTimerRef.current) clearTimeout(clickScrollTimerRef.current)
				clickScrollTimerRef.current = setTimeout(() => {
					isClickScrollingRef.current = false
					window.removeEventListener('scroll', onScrollIdle)
					scrollIdleListenerRef.current = null
					// If the click-scroll landed at the bottom of the page (e.g. a short last
					// category that cannot reach the IntersectionObserver active band), keep
					// the clicked category active rather than letting the observer revert it.
					const nearBottom =
						window.innerHeight + window.scrollY >= document.body.scrollHeight - 4
					if (nearBottom) {
						setActiveCategory(categoryRef)
					}
				}, SCROLL_IDLE_MS)
			}

			// Remove any stale listener from a previous click before registering the new one.
			if (scrollIdleListenerRef.current) {
				window.removeEventListener('scroll', scrollIdleListenerRef.current)
			}
			scrollIdleListenerRef.current = onScrollIdle
			window.addEventListener('scroll', onScrollIdle, { passive: true })
			// Fallback: if scrollIntoView targets a section already in view (no scroll fires),
			// release the flag after a short delay so the observer is not permanently blocked.
			clickScrollTimerRef.current = setTimeout(
				() => {
					isClickScrollingRef.current = false
					window.removeEventListener('scroll', onScrollIdle)
					scrollIdleListenerRef.current = null
				},
				reducedMotion ? 64 : 1500,
			)

			if (sheetOpen) {
				// The MUI Drawer scroll lock (overflow:hidden on body) is not released until
				// ~195ms after the Slide exit animation ends. Calling scrollIntoView while the
				// lock is active is silently discarded on iOS Safari and intermittently on Chrome.
				// Store the ref here and let the useEffect below fire the scroll once the lock
				// is gone. This covers both the plain-sheet case and sheet-open-with-search.
				pendingSheetScrollRef.current = categoryRef
			} else if (searchTerm) {
				// When search is active the category DOM nodes do not exist until MenuPage
				// re-renders the full list. Store the target ref in pendingScrollRef — a
				// useEffect watching searchTerm will scroll once React has committed the DOM.
				// A single rAF is not reliable in React 19 concurrent mode; the effect fires
				// after the commit phase regardless of how many render passes were needed.
				pendingScrollRef.current = categoryRef
			} else {
				document.getElementById(`category-${categoryRef}`)?.scrollIntoView({
					behavior: reducedMotion ? 'auto' : 'smooth',
					block: 'start',
				})
			}
		},
		[t, searchTerm, onSearch, sheetOpen],
	)

	// Execute deferred scroll once search is cleared and the full category DOM is committed.
	useEffect(() => {
		if (searchTerm !== '' || pendingScrollRef.current === null) return
		const categoryRef = pendingScrollRef.current
		pendingScrollRef.current = null
		const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
		document.getElementById(`category-${categoryRef}`)?.scrollIntoView({
			behavior: reducedMotion ? 'auto' : 'smooth',
			block: 'start',
		})
	}, [searchTerm])

	// Execute deferred scroll after the Drawer exit animation ends and its scroll lock is released.
	// The lock (overflow:hidden on body) is only removed ~195ms after the Slide animation finishes.
	// A 300ms delay from when sheetOpen becomes false gives the animation and teardown time to complete.
	// The cleanup cancels the timer if the user reopens the drawer before it fires.
	useEffect(() => {
		if (sheetOpen) return
		const categoryRef = pendingSheetScrollRef.current
		if (!categoryRef) return
		pendingSheetScrollRef.current = null
		const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
		const id = setTimeout(() => {
			document.getElementById(`category-${categoryRef}`)?.scrollIntoView({
				behavior: reducedMotion ? 'auto' : 'smooth',
				block: 'start',
			})
		}, 300)
		return () => clearTimeout(id)
	}, [sheetOpen])

	useEffect(() => {
		return () => {
			if (clickScrollTimerRef.current) clearTimeout(clickScrollTimerRef.current)
			if (scrollIdleListenerRef.current) {
				window.removeEventListener('scroll', scrollIdleListenerRef.current)
			}
		}
	}, [])

	// STO-3275: Dismiss virtual keyboard when the user taps outside the search row.
	// Scroll-to-dismiss was removed — the browser scrolls the viewport slightly when
	// the keyboard appears, which fired the once-scroll listener immediately and prevented
	// the user from re-focusing the input after the first search.
	useEffect(() => {
		if (!mobileSearchFocused) return

		function handlePointerDown(e: PointerEvent) {
			if (
				mobileSearchRowRef.current &&
				!mobileSearchRowRef.current.contains(e.target as Node)
			) {
				mobileSearchInputRef.current?.blur()
				setMobileSearchFocused(false)
			}
		}

		document.addEventListener('pointerdown', handlePointerDown)
		return () => {
			document.removeEventListener('pointerdown', handlePointerDown)
		}
	}, [mobileSearchFocused])

	function handleDesktopSearchChange(value: string) {
		setDesktopSearchValue(value)
		onSearch?.(value)
	}

	function handleDesktopSearchClear() {
		if (desktopSearchInputRef.current) {
			desktopSearchInputRef.current.value = ''
			desktopSearchInputRef.current.focus()
		}
		setDesktopSearchValue('')
		onSearch?.('')
	}

	function handleMobileSearchFocus() {
		setMobileSearchFocused(true)
	}

	function handleMobileSearchClear() {
		if (mobileSearchInputRef.current) {
			mobileSearchInputRef.current.value = ''
			mobileSearchInputRef.current.focus()
		}
		if (desktopSearchInputRef.current) desktopSearchInputRef.current.value = ''
		setMobileSearchValue('')
		setDesktopSearchValue('')
		onSearch?.('')
	}

	if (isLoading) return <CategoryNavSkeleton />
	if (categories.length === 0) return null

	const resolved = visibleCount ?? categories.length
	const hasOverflow = resolved < categories.length
	const visibleCategories = categories.slice(0, resolved)
	const overflowCategories = categories.slice(resolved)
	const isOverflowActive = overflowCategories.some((c) => c.ref === resolvedActive)
	const isDesktopSearchActive = desktopSearchValue.length > 0
	// Roving tabindex: when no category is active yet (resolvedActive is null — transient
	// loading state), fall back to the first visible pill so the nav is always Tab-reachable.
	// WCAG SC 2.1.2 requires keyboard focus is never trapped or lost.
	const firstPillRef = visibleCategories[0]?.ref ?? null

	return (
		<NavBar data-testid="category-nav">
			<MobileSearchRow ref={mobileSearchRowRef}>
				<MobileSearchBar>
					<SearchIcon sx={searchIconSx} />
					<SearchInput
						ref={mobileSearchInputRef}
						type="search"
						placeholder={t('component:categoryNav:searchMenu:placeholder')}
						aria-label={t('component:categoryNav:searchMenu:label')}
						autoComplete="off"
						maxLength={MENU_SEARCH_MAX_LENGTH}
						onFocus={handleMobileSearchFocus}
						onChange={(e) => {
							setMobileSearchValue(e.target.value)
							onSearch?.(e.target.value)
						}}
					/>
					{mobileSearchValue && (
						<SearchClearButton
							type="button"
							aria-label={t('component:categoryNav:clearSearch:label')}
							onClick={handleMobileSearchClear}
						>
							<CloseIcon sx={clearIconSx} />
						</SearchClearButton>
					)}
				</MobileSearchBar>
			</MobileSearchRow>

			<PillsRow>
				{!mobileSearchFocused && (
					<HamburgerButton
						aria-label={t('component:categoryNav:openCategoryMenu:label', {
							count: categories.length,
						})}
						aria-expanded={sheetOpen}
						onClick={() => setSheetOpen(true)}
					>
						<MenuIcon sx={primaryIconSx} />
					</HamburgerButton>
				)}

				<MobilePillRow
					ref={mobilePillsRef}
					aria-label={t('component:categoryNav:categoriesMobile:label')}
					sx={mobileSearchFocused ? { display: 'none' } : undefined}
				>
					{categories.map((category) => {
						const isActive = category.ref === resolvedActive
						return (
							<PillButton
								key={category.ref}
								isActive={isActive}
								aria-current={isActive ? 'page' : undefined}
								onPointerDown={handleCategoryPointerDown}
								onClick={() => handleCategoryClick(category.ref, category.name)}
							>
								{category.name}
							</PillButton>
						)
					})}
				</MobilePillRow>

				<PillsWrap
					ref={pillsWrapRef}
					role="toolbar"
					aria-label={t('component:categoryNav:categoriesDesktop:label')}
					data-testid="category-nav-desktop"
				>
					<MeasureRow ref={measureRowRef} aria-hidden="true">
						{categories.map((category) => (
							<PillButton
								key={`measure-${category.ref}`}
								data-measure
								tabIndex={-1}
								isActive={false}
								style={{ pointerEvents: 'none' }}
							>
								{category.name}
							</PillButton>
						))}
						<MoreButton
							data-measure
							tabIndex={-1}
							isActive={false}
							style={{ pointerEvents: 'none' }}
						>
							{t('component:categoryNav:more:label')}
							<ExpandMoreIcon sx={primaryIconSx} />
						</MoreButton>
					</MeasureRow>

					{visibleCategories.map((category) => {
						const isActive = category.ref === resolvedActive
						// Roving tabindex: active pill is the tab stop. If resolvedActive is null
						// (transient loading state), fall back to the first pill so the nav is
						// always reachable by keyboard — WCAG SC 2.1.2.
						const isTabStop =
							isActive || (!resolvedActive && category.ref === firstPillRef)
						return (
							<PillButton
								key={category.ref}
								isActive={isActive}
								aria-current={isActive ? 'page' : undefined}
								tabIndex={isTabStop ? 0 : -1}
								onPointerDown={handleCategoryPointerDown}
								onKeyDown={handleDesktopPillKeyDown}
								onClick={() => handleCategoryClick(category.ref, category.name)}
							>
								{category.name}
							</PillButton>
						)
					})}

					{hasOverflow && (
						<MoreButton
							ref={moreButtonRef}
							isActive={isOverflowActive}
							// More button participates in roving tabindex — active if an overflow
							// category is selected, otherwise reachable via Arrow Right from last pill.
							// ArrowDown opens the dropdown; Left/Right/Home/End move within the pill row.
							tabIndex={isOverflowActive ? 0 : -1}
							aria-haspopup="menu"
							aria-expanded={moreOpen}
							onKeyDown={handleMoreButtonKeyDown}
							onClick={() => setMoreOpen((o) => !o)}
						>
							{t('component:categoryNav:more:label')}
							<ExpandMoreIcon sx={moreIconSx} />
						</MoreButton>
					)}
				</PillsWrap>

				<SearchBar isActive={isDesktopSearchActive}>
					<SearchIcon sx={searchIconSx} />
					<SearchInput
						ref={desktopSearchInputRef}
						type="search"
						placeholder={t('component:categoryNav:searchMenu:placeholder')}
						aria-label={t('component:categoryNav:searchMenu:label')}
						autoComplete="off"
						maxLength={MENU_SEARCH_MAX_LENGTH}
						onChange={(e) => handleDesktopSearchChange(e.target.value)}
					/>
					{isDesktopSearchActive && (
						<SearchClearButton
							type="button"
							aria-label={t('component:categoryNav:clearSearch:label')}
							onClick={handleDesktopSearchClear}
						>
							<CloseIcon sx={clearIconSx} />
						</SearchClearButton>
					)}
				</SearchBar>
			</PillsRow>

			<Popper
				open={moreOpen}
				anchorEl={moreAnchorEl}
				placement="bottom-start"
				disablePortal
				sx={{ zIndex: 'tooltip' }}
			>
				<ClickAwayListener onClickAway={() => setMoreOpen(false)}>
					<MoreMenu
						ref={moreMenuRef}
						role="menu"
						tabIndex={-1}
						aria-label={t('component:categoryNav:moreCategories:label')}
						onKeyDown={handleMoreMenuKeyDown}
					>
						{overflowCategories.map((category) => {
							const isActive = category.ref === resolvedActive
							return (
								<MoreMenuItem
									key={category.ref}
									role="menuitem"
									tabIndex={-1}
									isActive={isActive}
									aria-current={isActive ? 'page' : undefined}
									onPointerDown={handleCategoryPointerDown}
									onClick={() => handleCategoryClick(category.ref, category.name)}
								>
									{category.name}
								</MoreMenuItem>
							)
						})}
					</MoreMenu>
				</ClickAwayListener>
			</Popper>

			<Drawer
				anchor="bottom"
				open={sheetOpen}
				onClose={() => setSheetOpen(false)}
				slotProps={{
					paper: {
						role: 'dialog',
						'aria-label': t('component:categoryNav:menuCategoriesSheet:label'),
						sx: SheetPaperSx,
					},
				}}
			>
				<MobileSheetHeader>
					<MobileSheetTitle>
						{t('component:categoryNav:menuSheet:title')}
					</MobileSheetTitle>
					<MobileSheetCloseButton
						aria-label={t('component:categoryNav:closeCategoryMenu:label')}
						onClick={() => setSheetOpen(false)}
					>
						<CloseIcon sx={primaryIconSx} />
					</MobileSheetCloseButton>
				</MobileSheetHeader>
				<MobileSheetList>
					{categories.map((category) => {
						const isActive = category.ref === resolvedActive
						return (
							<MobileSheetItem
								key={category.ref}
								isActive={isActive}
								aria-current={isActive ? 'page' : undefined}
								onPointerDown={handleCategoryPointerDown}
								onClick={() => handleCategoryClick(category.ref, category.name)}
							>
								{category.name}
							</MobileSheetItem>
						)
					})}
				</MobileSheetList>
				<MobileSheetCancel onClick={() => setSheetOpen(false)}>
					{t('component:categoryNav:cancel:label')}
				</MobileSheetCancel>
			</Drawer>

			{/* Visually hidden live region for screen reader announcements */}
			<LiveRegion role="status" aria-live="polite" aria-atomic="true">
				{announcement}
			</LiveRegion>
		</NavBar>
	)
}
